'use client';

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Trash, 
  Table,
} from '@phosphor-icons/react';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { useNodeInputColumns } from '../../../hooks/useNodeInputs';
import { useNodeSchema } from '../../../contexts/SchemaContext';
import type { Column } from '../../../types';
import { getNodeDisplayLabel } from '../../../utils/nodeDisplayLabel';

type PivotMode = 'pivot_table' | 'pivot' | 'unpivot';
type AggFunction = 'none' | 'sum' | 'count' | 'avg' | 'max' | 'min' | 'distinct_count' | 'product' | 'custom';

interface PivotValueField {
  column: string;
  function: AggFunction;
  alias: string;
}

export interface PivotConfigParams {
  mode?: PivotMode;
  rows?: string[];
  columns?: string[];
  values?: PivotValueField[];
  indexField?: string;
  columnField?: string;
  valueField?: string;
  pivotAggregation?: AggFunction;
  customFormula?: string;
  keepFields?: string[];
  unpivotFields?: string[];
  fieldName?: string;
  valueName?: string;
  fillValue?: 'zero' | 'empty' | 'custom';
  customFillValue?: string;
}

const AGG_FUNCTIONS: { value: AggFunction; labelKey: string }[] = [
  { value: 'none', labelKey: 'nodes.config.pivot.none' },
  { value: 'sum', labelKey: 'nodes.config.pivot.sum' },
  { value: 'count', labelKey: 'nodes.config.pivot.count' },
  { value: 'avg', labelKey: 'nodes.config.pivot.avg' },
  { value: 'max', labelKey: 'nodes.config.pivot.max' },
  { value: 'min', labelKey: 'nodes.config.pivot.min' },
  { value: 'distinct_count', labelKey: 'nodes.config.pivot.distinctCount' },
  { value: 'product', labelKey: 'nodes.config.pivot.product' },
  { value: 'custom', labelKey: 'nodes.config.pivot.custom' },
];

const FILL_OPTIONS: { value: 'zero' | 'empty' | 'custom'; labelKey: string }[] = [
  { value: 'zero', labelKey: 'nodes.config.pivot.fillZero' },
  { value: 'empty', labelKey: 'nodes.config.pivot.fillEmpty' },
  { value: 'custom', labelKey: 'nodes.config.pivot.fillCustom' },
];

interface PivotConfigProps {
  nodeId: string;
  config: PivotConfigParams;
}

export function PivotConfig({ nodeId, config }: PivotConfigProps) {
  const { t, i18n } = useTranslation();
  const { nodes, updateNodeConfig } = useWorkflowStore();
  const { register } = useNodeSchema(nodeId);
  const inputColumns = useNodeInputColumns(nodeId);
  
  // 提取列名列表和连接状态
  const columns = useMemo(() => {
    if (!inputColumns) return [];
    return inputColumns.map(ic => ic.column.name);
  }, [inputColumns]);
  
  const isConnected = inputColumns !== undefined;

  const [mode, setMode] = useState<PivotMode>(config.mode || 'pivot_table');
  const [rows, setRows] = useState<string[]>(config.rows || []);
  const [cols, setCols] = useState<string[]>(config.columns || []);
  const [values, setValues] = useState<PivotValueField[]>(config.values?.length ? config.values : [{ column: '', function: 'sum', alias: '' }]);
  const [indexField, setIndexField] = useState(config.indexField || '');
  const [columnField, setColumnField] = useState(config.columnField || '');
  const [valueField, setValueField] = useState(config.valueField || '');
  const [pivotAggregation, setPivotAggregation] = useState<AggFunction>(config.pivotAggregation || 'none');
  const [customFormula, setCustomFormula] = useState(config.customFormula || '');
  const [keepFields, setKeepFields] = useState<string[]>(config.keepFields || []);
  const [unpivotFields, setUnpivotFields] = useState<string[]>(config.unpivotFields || []);
  const [fieldName, setFieldName] = useState(config.fieldName || '维度');
  const [valueName, setValueName] = useState(config.valueName || '数值');
  const [fillValue, setFillValue] = useState<'zero' | 'empty' | 'custom'>((config.fillValue as 'zero' | 'empty' | 'custom') || 'zero');
  const [customFillValue, setCustomFillValue] = useState(config.customFillValue || '');

  // 当 config 变化时（如 AI 生成工作流后），同步更新内部状态
  useEffect(() => {
    if (config.mode) setMode(config.mode);
    if (config.rows) setRows(config.rows);
    if (config.columns) setCols(config.columns);
    if (config.values) setValues(config.values);
    if (config.indexField) setIndexField(config.indexField);
    if (config.columnField) setColumnField(config.columnField);
    if (config.valueField) setValueField(config.valueField);
    if (config.pivotAggregation) setPivotAggregation(config.pivotAggregation);
    if (config.customFormula) setCustomFormula(config.customFormula);
    if (config.keepFields) setKeepFields(config.keepFields);
    if (config.unpivotFields) setUnpivotFields(config.unpivotFields);
    if (config.fieldName) setFieldName(config.fieldName);
    if (config.valueName) setValueName(config.valueName);
    if (config.fillValue) setFillValue(config.fillValue as 'zero' | 'empty' | 'custom');
    if (config.customFillValue) setCustomFillValue(config.customFillValue);
  }, [config]);

  // 转换 fillValue 为后端期望的格式
  const getFillValueForBackend = (fv: 'zero' | 'empty' | 'custom', customVal: string): number | string | null => {
    switch (fv) {
      case 'zero':
        return 0;
      case 'empty':
        return null;
      case 'custom':
        // 尝试转换为数字，如果失败则保持字符串
        const num = Number(customVal);
        return !isNaN(num) && customVal !== '' ? num : customVal;
      default:
        return 0;
    }
  };

  const saveConfig = (updates: Partial<PivotConfigParams>) => {
    const newMode = updates.mode ?? mode;
    const newRows = updates.rows ?? rows;
    const newCols = updates.columns ?? cols;
    const newValues = updates.values ?? values;
    const newIndexField = updates.indexField ?? indexField;
    const newColumnField = updates.columnField ?? columnField;
    const newValueField = updates.valueField ?? valueField;
    const newPivotAggregation = updates.pivotAggregation ?? pivotAggregation;
    const newCustomFormula = updates.customFormula ?? customFormula;
    const newKeepFields = updates.keepFields ?? keepFields;
    const newUnpivotFields = updates.unpivotFields ?? unpivotFields;
    const newFieldName = updates.fieldName ?? fieldName;
    const newValueName = updates.valueName ?? valueName;
    const newFillValue = updates.fillValue ?? fillValue;
    const newCustomFillValue = updates.customFillValue ?? customFillValue;
    
    if (updates.mode) setMode(updates.mode);
    if (updates.rows) setRows(updates.rows);
    if (updates.columns) setCols(updates.columns);
    if (updates.values) setValues(updates.values);
    if (updates.indexField !== undefined) setIndexField(updates.indexField);
    if (updates.columnField !== undefined) setColumnField(updates.columnField);
    if (updates.valueField !== undefined) setValueField(updates.valueField);
    if (updates.pivotAggregation) setPivotAggregation(updates.pivotAggregation);
    if (updates.customFormula !== undefined) setCustomFormula(updates.customFormula);
    if (updates.keepFields) setKeepFields(updates.keepFields);
    if (updates.unpivotFields) setUnpivotFields(updates.unpivotFields);
    if (updates.fieldName !== undefined) setFieldName(updates.fieldName);
    if (updates.valueName !== undefined) setValueName(updates.valueName);
    if (updates.fillValue) setFillValue(updates.fillValue);
    if (updates.customFillValue !== undefined) setCustomFillValue(updates.customFillValue);

    // 构建发送给后端的配置
    const backendConfig: Record<string, unknown> = {
      mode: newMode,
      rows: newRows,
      columns: newCols,
      values: newValues,
      indexField: newIndexField,
      columnField: newColumnField,
      valueField: newValueField,
      pivotAggregation: newPivotAggregation,
      customFormula: newCustomFormula,
      keepFields: newKeepFields,
      unpivotFields: newUnpivotFields,
      fieldName: newFieldName,
      valueName: newValueName,
      fillValue: getFillValueForBackend(newFillValue, newCustomFillValue),
    };

    updateNodeConfig(nodeId, {
      type: 'pivot',
      params: backendConfig,
    });

    registerOutputSchema(newMode, newRows, newCols, newValues, newIndexField, newColumnField, newValueField, newKeepFields, newUnpivotFields, newFieldName, newValueName);
  };

  const registerOutputSchema = useCallback((
    m: PivotMode,
    rowFields: string[],
    colFields: string[],
    valueFields: PivotValueField[],
    idxField: string,
    _colField: string,
    _valField: string,
    keptFields: string[],
    _unpivotCols: string[],
    fName: string,
    vName: string,
  ) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    let outputCols: Column[] = [];

    if (m === 'pivot_table') {
      let idx = 0;
      rowFields.forEach(r => {
        const upstream = inputColumns?.find(ic => ic.column.name === r);
        outputCols.push({
          name: r,
          type: upstream?.column.type ?? 'string',
          nullable: true,
          index: idx++,
        });
      });
      const validValues = valueFields.filter(v => v.column);
      if (colFields.length > 0 && validValues.length > 0) {
        validValues.forEach(v => {
          const label = v.alias || `${v.column}_${v.function}`;
          outputCols.push({
            name: label,
            type: 'number',
            nullable: true,
            index: idx++,
            dynamicGroup: label,
          });
        });
      } else if (validValues.length > 0) {
        validValues.forEach(v => {
          const label = v.alias || `${v.column}_${v.function}`;
          outputCols.push({ name: label, type: 'number', nullable: true, index: idx++ });
        });
      }
    } else if (m === 'pivot') {
      let idx = 0;
      if (idxField) {
        const upstream = inputColumns?.find(ic => ic.column.name === idxField);
        outputCols.push({
          name: idxField,
          type: upstream?.column.type ?? 'string',
          nullable: true,
          index: idx++,
        });
      }
      outputCols.push({
        name: 'pivoted_value',
        type: 'string',
        nullable: true,
        index: idx++,
        dynamicGroup: 'pivoted_value',
      });
    } else if (m === 'unpivot') {
      let idx = 0;
      keptFields.forEach(k => {
        const upstream = inputColumns?.find(ic => ic.column.name === k);
        outputCols.push({
          name: k,
          type: upstream?.column.type ?? 'string',
          nullable: true,
          index: idx++,
        });
      });
      outputCols.push({
        name: fName || '维度',
        type: 'string',
        nullable: true,
        index: idx++,
      });
      outputCols.push({
        name: vName || '数值',
        type: 'string',
        nullable: true,
        index: idx++,
      });
    }

    if (outputCols.length > 0) {
      const dynamicGroups = outputCols
        .filter(c => c.dynamicGroup)
        .map(c => ({ commonName: c.dynamicGroup!, physicalColumns: [c.name] }));

      register({
        nodeType: node.type,
        nodeName: getNodeDisplayLabel(node.type, node.data?.label as string | undefined, t, i18n),
        columns: outputCols,
        dynamicGroups: dynamicGroups.length > 0 ? dynamicGroups : undefined,
      });
    }
  }, [nodeId, nodes, inputColumns, register, t, i18n]);

  useEffect(() => {
    registerOutputSchema(mode, rows, cols, values, indexField, columnField, valueField, keepFields, unpivotFields, fieldName, valueName);
  }, [mode, rows, cols, values, indexField, columnField, valueField, keepFields, unpivotFields, fieldName, valueName, registerOutputSchema]);

  const handleModeChange = (newMode: PivotMode) => {
    saveConfig({ mode: newMode });
  };

  const modeOptions = useMemo(() => [
    { value: 'pivot_table' as PivotMode, label: t('nodes.config.pivot.modePivotTable'), desc: t('nodes.config.pivot.modePivotTableDesc'), icon: Table },
    // { value: 'pivot' as PivotMode, label: t('nodes.config.pivot.modePivot'), desc: t('nodes.config.pivot.modePivotDesc'), icon: ArrowsHorizontal },
    // { value: 'unpivot' as PivotMode, label: t('nodes.config.pivot.modeUnpivot'), desc: t('nodes.config.pivot.modeUnpivotDesc'), icon: ArrowsVertical },
  ], [t]);

  const toggleSelection = (current: string[], value: string, setter: (v: string[]) => void, key: string) => {
    const newValues = current.includes(value) 
      ? current.filter(v => v !== value)
      : [...current, value];
    setter(newValues);
    saveConfig({ [key]: newValues } as Partial<PivotConfigParams>);
  };

  const addValueField = () => {
    const newValues = [...values, { column: '', function: 'sum' as AggFunction, alias: '' }];
    saveConfig({ values: newValues });
  };

  const removeValueField = (index: number) => {
    const newValues = values.filter((_, i) => i !== index);
    saveConfig({ values: newValues.length ? newValues : [{ column: '', function: 'sum', alias: '' }] });
  };

  const updateValueField = (index: number, field: Partial<PivotValueField>) => {
    const newValues = values.map((v, i) => i === index ? { ...v, ...field } : v);
    saveConfig({ values: newValues });
  };

  return (
    <div className="space-y-5">
      {/* Mode Selection */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-zinc-500">
          {t('nodes.config.pivot.mode')}
        </label>
        <div className="space-y-1.5">
          {modeOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleModeChange(option.value)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-all ${
                mode === option.value
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50'
              }`}
            >
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                mode === option.value ? 'border-emerald-500' : 'border-zinc-300'
              }`}>
                {mode === option.value && (
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                )}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium">{option.label}</div>
                <div className={`text-[10px] ${mode === option.value ? 'text-emerald-600' : 'text-zinc-400'}`}>
                  {option.desc}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {!isConnected ? (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-sm text-amber-700">{t('nodes.config.pivot.connectInputFirst')}</p>
        </div>
      ) : (
        <AnimatePresence mode="wait">
          {mode === 'pivot_table' && (
            <motion.div
              key="pivot_table"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              {/* Rows */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-zinc-500">{t('nodes.config.pivot.rows')}</label>
                <div className="flex flex-wrap gap-1.5 p-2 border border-zinc-200 rounded-lg bg-white min-h-[40px]">
                  {columns.map(col => (
                    <button
                      key={col}
                      onClick={() => toggleSelection(rows, col, setRows, 'rows')}
                      className={`px-2 py-1 rounded-md text-xs transition-all ${
                        rows.includes(col)
                          ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                          : 'bg-zinc-50 text-zinc-600 border border-zinc-200 hover:bg-zinc-100'
                      }`}
                    >
                      {col}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-400">{t('nodes.config.pivot.rowsHint')}</p>
              </div>

              {/* Columns */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-zinc-500">{t('nodes.config.pivot.columns')}</label>
                <div className="flex flex-wrap gap-1.5 p-2 border border-zinc-200 rounded-lg bg-white min-h-[40px]">
                  {columns.map(col => (
                    <button
                      key={col}
                      onClick={() => toggleSelection(cols, col, setCols, 'columns')}
                      className={`px-2 py-1 rounded-md text-xs transition-all ${
                        cols.includes(col)
                          ? 'bg-blue-100 text-blue-700 border border-blue-300'
                          : 'bg-zinc-50 text-zinc-600 border border-zinc-200 hover:bg-zinc-100'
                      }`}
                    >
                      {col}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-400">{t('nodes.config.pivot.columnsHint')}</p>
              </div>

              {/* Values */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-zinc-500">{t('nodes.config.pivot.values')}</label>
                  <button
                    onClick={addValueField}
                    className="p-1 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-2">
                  {values.map((value, index) => (
                    <div key={index} className="p-3 bg-zinc-50 rounded-lg border border-zinc-200 space-y-2">
                      <div className="flex items-center gap-2">
                        <select
                          value={value.column}
                          onChange={(e) => updateValueField(index, { column: e.target.value })}
                          className="flex-1 px-2 py-1.5 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                        >
                          <option value="">{t('nodes.config.pivot.selectColumn')}</option>
                          {columns.map(col => (
                            <option key={col} value={col}>{col}</option>
                          ))}
                        </select>
                        {values.length > 1 && (
                          <button
                            onClick={() => removeValueField(index)}
                            className="p-1.5 text-zinc-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                          >
                            <Trash className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={value.function}
                          onChange={(e) => updateValueField(index, { function: e.target.value as AggFunction })}
                          className="flex-1 px-2 py-1.5 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                        >
                          {AGG_FUNCTIONS.map(func => (
                            <option key={func.value} value={func.value}>{t(func.labelKey)}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={value.alias}
                          onChange={(e) => updateValueField(index, { alias: e.target.value })}
                          placeholder={t('nodes.config.pivot.aliasPlaceholder')}
                          className="flex-1 px-2 py-1.5 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-400">{t('nodes.config.pivot.valuesHint')}</p>
              </div>

              {/* Fill Value */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-zinc-500">{t('nodes.config.pivot.fillValue')}</label>
                <div className="flex gap-2">
                  {FILL_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      onClick={() => saveConfig({ fillValue: option.value })}
                      className={`flex-1 px-3 py-2 text-xs rounded-lg border transition-all ${
                        fillValue === option.value
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                          : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300'
                      }`}
                    >
                      {t(option.labelKey)}
                    </button>
                  ))}
                </div>
                {fillValue === 'custom' && (
                  <input
                    type="text"
                    value={customFillValue}
                    onChange={(e) => saveConfig({ customFillValue: e.target.value })}
                    placeholder={t('nodes.config.pivot.customValue')}
                    className="w-full px-3 py-2 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                )}
                <p className="text-[10px] text-zinc-400">{t('nodes.config.pivot.fillValueHint')}</p>
              </div>

              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xs text-blue-600">{t('nodes.config.pivot.pivotTip')}</p>
              </div>
            </motion.div>
          )}

          {mode === 'pivot' && (
            <motion.div
              key="pivot"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              {/* Index Field */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-zinc-500">{t('nodes.config.pivot.indexField')}</label>
                <select
                  value={indexField}
                  onChange={(e) => saveConfig({ indexField: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                >
                  <option value="">{t('nodes.config.pivot.selectIndexField')}</option>
                  {columns.map(col => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
                <p className="text-[10px] text-zinc-400">{t('nodes.config.pivot.indexFieldHint')}</p>
              </div>

              {/* Column Field */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-zinc-500">{t('nodes.config.pivot.columnField')}</label>
                <select
                  value={columnField}
                  onChange={(e) => saveConfig({ columnField: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                >
                  <option value="">{t('nodes.config.pivot.selectColumnField')}</option>
                  {columns.map(col => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
                <p className="text-[10px] text-zinc-400">{t('nodes.config.pivot.columnFieldHint')}</p>
              </div>

              {/* Value Field */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-zinc-500">{t('nodes.config.pivot.valueField')}</label>
                <select
                  value={valueField}
                  onChange={(e) => saveConfig({ valueField: e.target.value })}
                  className="w-full px-3 py-2 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                >
                  <option value="">{t('nodes.config.pivot.selectValueField')}</option>
                  {columns.map(col => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
                <p className="text-[10px] text-zinc-400">{t('nodes.config.pivot.valueFieldHint')}</p>
              </div>

              {/* Pivot Aggregation Function */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-zinc-500">{t('nodes.config.pivot.aggregation')}</label>
                <select
                  value={pivotAggregation}
                  onChange={(e) => saveConfig({ pivotAggregation: e.target.value as AggFunction })}
                  className="w-full px-3 py-2 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                >
                  {AGG_FUNCTIONS.map(func => (
                    <option key={func.value} value={func.value}>{t(func.labelKey)}</option>
                  ))}
                </select>
                <p className="text-[10px] text-zinc-400">{t('nodes.config.pivot.aggregationHint')}</p>
              </div>

              {/* Custom Formula (only shown when custom aggregation is selected) */}
              {pivotAggregation === 'custom' && (
                <div className="space-y-2">
                  <label className="text-xs font-medium text-zinc-500">{t('nodes.config.pivot.customFormula')}</label>
                  <input
                    type="text"
                    value={customFormula}
                    onChange={(e) => saveConfig({ customFormula: e.target.value })}
                    placeholder={t('nodes.config.pivot.customFormulaPlaceholder')}
                    className="w-full px-3 py-2 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                  <p className="text-[10px] text-zinc-400">{t('nodes.config.pivot.customFormulaHint')}</p>
                </div>
              )}

              {/* Keep Fields */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-zinc-500">{t('nodes.config.pivot.keepFields')}</label>
                <div className="flex flex-wrap gap-1.5 p-2 border border-zinc-200 rounded-lg bg-white min-h-[40px]">
                  {columns.map(col => (
                    <button
                      key={col}
                      onClick={() => toggleSelection(keepFields, col, setKeepFields, 'keepFields')}
                      className={`px-2 py-1 rounded-md text-xs transition-all ${
                        keepFields.includes(col)
                          ? 'bg-violet-100 text-violet-700 border border-violet-300'
                          : 'bg-zinc-50 text-zinc-600 border border-zinc-200 hover:bg-zinc-100'
                      }`}
                    >
                      {col}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-400">{t('nodes.config.pivot.keepFieldsHint')}</p>
              </div>

              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xs text-blue-600">{t('nodes.config.pivot.pivotTip2')}</p>
              </div>
            </motion.div>
          )}

          {mode === 'unpivot' && (
            <motion.div
              key="unpivot"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              {/* Unpivot Fields */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-zinc-500">{t('nodes.config.pivot.unpivotFields')}</label>
                <div className="flex flex-wrap gap-1.5 p-2 border border-zinc-200 rounded-lg bg-white min-h-[40px]">
                  {columns.map(col => (
                    <button
                      key={col}
                      onClick={() => toggleSelection(unpivotFields, col, setUnpivotFields, 'unpivotFields')}
                      className={`px-2 py-1 rounded-md text-xs transition-all ${
                        unpivotFields.includes(col)
                          ? 'bg-orange-100 text-orange-700 border border-orange-300'
                          : 'bg-zinc-50 text-zinc-600 border border-zinc-200 hover:bg-zinc-100'
                      }`}
                    >
                      {col}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-400">{t('nodes.config.pivot.unpivotFieldsHint')}</p>
              </div>

              {/* Field Name */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-zinc-500">{t('nodes.config.pivot.fieldName')}</label>
                <input
                  type="text"
                  value={fieldName}
                  onChange={(e) => saveConfig({ fieldName: e.target.value })}
                  placeholder={t('nodes.config.pivot.fieldNamePlaceholder')}
                  className="w-full px-3 py-2 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
                <p className="text-[10px] text-zinc-400">{t('nodes.config.pivot.fieldNameHint')}</p>
              </div>

              {/* Value Name */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-zinc-500">{t('nodes.config.pivot.valueName')}</label>
                <input
                  type="text"
                  value={valueName}
                  onChange={(e) => saveConfig({ valueName: e.target.value })}
                  placeholder={t('nodes.config.pivot.valueNamePlaceholder')}
                  className="w-full px-3 py-2 text-xs bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
                <p className="text-[10px] text-zinc-400">{t('nodes.config.pivot.valueNameHint')}</p>
              </div>

              {/* Keep Fields */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-zinc-500">{t('nodes.config.pivot.keepFields')}</label>
                <div className="flex flex-wrap gap-1.5 p-2 border border-zinc-200 rounded-lg bg-white min-h-[40px]">
                  {columns.map(col => (
                    <button
                      key={col}
                      onClick={() => toggleSelection(keepFields, col, setKeepFields, 'keepFields')}
                      className={`px-2 py-1 rounded-md text-xs transition-all ${
                        keepFields.includes(col)
                          ? 'bg-violet-100 text-violet-700 border border-violet-300'
                          : 'bg-zinc-50 text-zinc-600 border border-zinc-200 hover:bg-zinc-100'
                      }`}
                    >
                      {col}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-400">{t('nodes.config.pivot.keepFieldsHint')}</p>
              </div>

              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-xs text-blue-600">{t('nodes.config.pivot.unpivotTip')}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
}
